import React, { useCallback, useEffect, useState } from "react";
import { MessagesSquare, Loader2, Send, ChevronDown } from "lucide-react";
import api from "../../api";
import { useLang } from "../../context/LanguageContext";
import { useToast } from "../../hooks/use-toast";

// An email you can't reply to is a dead end. Everything LytePress sends lives here as a thread.
export const MyMessagesCard = () => {
  const { t } = useLang();
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState("");
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/my/messages").then((r) => setData(r.data)).catch(() => setData(null)), []);
  useEffect(() => { load(); }, [load]);

  const expand = async (id) => {
    const next = open === id ? "" : id;
    setOpen(next);
    setDraft("");
    if (next) { await api.post(`/my/messages/${next}/read`).catch(() => {}); load(); }
  };

  const reply = async (id) => {
    setBusy(true);
    try {
      await api.post(`/my/messages/${id}/reply`, { message: draft.trim() });
      setDraft("");
      toast({ title: t("msg.sent") });
      load();
    } catch (e) { toast({ title: e.response?.data?.detail || t("acc.pleaseTryAgain") }); }
    finally { setBusy(false); }
  };

  if (!data || !data.threads?.length) return null;

  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="my-messages-card">
      <h3 className="font-bold text-foreground flex items-center gap-2">
        <MessagesSquare size={18} className="text-[#16a34a]" /> {t("msg.title")}
        {data.unread > 0 && (
          <span className="text-[11px] font-bold bg-[#16a34a] text-white rounded-full px-2 py-0.5" data-testid="my-messages-unread">
            {data.unread} {t("msg.new")}
          </span>
        )}
      </h3>
      <p className="text-xs text-muted-foreground mt-0.5 mb-3">{t("msg.sub")}</p>

      <div className="divide-y divide-border/60">
        {data.threads.map((th) => (
          <div key={th.id} className="py-3" data-testid={`my-message-${th.id}`}>
            <button onClick={() => expand(th.id)} data-testid={`my-message-open-${th.id}`}
              className="w-full flex items-start gap-2 text-left">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-foreground truncate">
                  {th.unread && <span className="inline-block h-2 w-2 rounded-full bg-[#16a34a] mr-1.5 align-middle" />}
                  {th.subject || t("msg.noSubject")}
                </p>
                <p className="text-xs text-muted-foreground truncate">{th.message}</p>
              </div>
              <ChevronDown size={16} className={`shrink-0 mt-1 text-muted-foreground transition-transform ${open === th.id ? "rotate-180" : ""}`} />
            </button>

            {open === th.id && (
              <div className="mt-3 space-y-2">
                <div className="bg-secondary rounded-xl p-3 text-sm text-foreground whitespace-pre-wrap">{th.message}</div>
                {th.replies.map((r, i) => (
                  <div key={i} className={`rounded-xl p-3 text-sm whitespace-pre-wrap ${r.mine
                    ? "bg-[#e7f6ec] dark:bg-[#16341f] text-foreground ml-6"
                    : "bg-secondary text-foreground mr-6"}`} data-testid={`my-message-reply-${th.id}-${i}`}>
                    <p className="text-[11px] font-bold text-muted-foreground mb-1">
                      {r.mine ? t("msg.you") : "LytePress"} · {r.at ? new Date(r.at).toLocaleString() : ""}
                    </p>
                    {r.message}
                  </div>
                ))}
                <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={3}
                  placeholder={t("msg.replyPlaceholder")} data-testid={`my-message-input-${th.id}`}
                  className="w-full bg-secondary rounded-xl px-3 py-2 text-sm outline-none resize-none" />
                <button onClick={() => reply(th.id)} disabled={busy || draft.trim().length < 2}
                  data-testid={`my-message-send-${th.id}`}
                  className="inline-flex items-center gap-1.5 text-sm font-bold bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white px-4 py-2 rounded-xl transition-colors">
                  {busy ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />} {t("msg.send")}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MyMessagesCard;
